import { FormControl, FormGroup, Validators } from '@angular/forms';
import { OTHER_OPTION, otherTextRequiredValidator } from '../../validators/vehicle-form.validators';
import { environment } from '../../environments/environment';

export { OTHER_OPTION };

/** PascalCase to match the API's CreateVehicleDto/UpdateVehicleDto fields. */
export interface VehicleFormValue {
  Year: string;
  Make: string;
  Model: string;
  Vin?: string;
  LicensePlate?: string;
  PlateState?: string;
}

/**
 * Single source of truth for the vehicle form used by both the add-vehicle
 * modal and the vehicle-detail edit form. Year/Make/Model are required;
 * VIN and license plate are optional (odd vehicles may lack registration),
 * with VIN format-checked when present.
 */
export function buildVehicleForm(initial?: Partial<VehicleFormValue>): FormGroup {
  return new FormGroup({
    year: new FormControl(initial?.Year ?? '', [Validators.required, Validators.pattern(/^\d{4}$/)]),
    make: new FormControl(initial?.Make ?? '', [Validators.required]),
    makeOther: new FormControl(''),
    model: new FormControl(initial?.Model ?? '', [Validators.required]),
    modelOther: new FormControl(''),
    vin: new FormControl(initial?.Vin ?? '', [Validators.pattern(/^[A-HJ-NPR-Z0-9]+$/i)]),
    licensePlate: new FormControl(initial?.LicensePlate ?? '', [Validators.maxLength(20)]),
    plateState: new FormControl(initial?.PlateState ?? environment.defaultState, [Validators.pattern(/^[A-Za-z]{2}$/)]),
  }, { validators: [otherTextRequiredValidator] });
}

/** Resolve the form into DTO fields: "Other…" folds in its free text, VIN is uppercased, blanks are omitted. */
export function vehicleFormValue(form: FormGroup): VehicleFormValue {
  const value = form.getRawValue();
  const trimmedOrUndefined = (s?: string | null) => {
    const trimmed = (s ?? '').trim();
    return trimmed || undefined;
  };
  const licensePlate = trimmedOrUndefined(value.licensePlate);
  return {
    Year: (value.year ?? '').trim(),
    Make: ((value.make === OTHER_OPTION ? value.makeOther : value.make) ?? '').trim(),
    Model: ((value.model === OTHER_OPTION ? value.modelOther : value.model) ?? '').trim(),
    Vin: trimmedOrUndefined(value.vin)?.toUpperCase(),
    LicensePlate: licensePlate,
    // A plate state only means something alongside a plate.
    PlateState: licensePlate ? trimmedOrUndefined(value.plateState)?.toUpperCase() : undefined,
  };
}

/**
 * PATCH variant: blank VIN/plate become '' (instead of omitted) so the server
 * treats them as an explicit clear rather than "unchanged".
 */
export function vehicleFormPatchValue(form: FormGroup): VehicleFormValue {
  const value = vehicleFormValue(form);
  return {
    ...value,
    Vin: value.Vin ?? '',
    LicensePlate: value.LicensePlate ?? '',
    PlateState: value.PlateState ?? '',
  };
}

/** Newest first, next model year down to 1950 — mechanic shops see classics. */
export function buildYearOptions(): string[] {
  const max = new Date().getFullYear() + 1;
  return Array.from({ length: max - 1950 + 1 }, (_, i) => String(max - i));
}
